import { faSpinnerThird } from "@fortawesome/pro-solid-svg-icons";
import { faCaretRight, faCheck } from "@fortawesome/sharp-solid-svg-icons";
import {
  Box,
  ListItemIcon,
  ListItemIconProps,
  ListItemText,
  MenuItem as MuiMenuItem,
  MenuItemProps,
  MenuProps,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useState } from "react";

import { Icon } from "../../Icon/Icon";

declare module "@mui/material/MenuItem" {
  interface MenuItemOwnProps {
    variant?: "primary" | "secondary" | "tertiary";
    icon?: React.ReactElement;
    showSelectedIcon?: boolean;
    endLabel?: string;
    endIcon?: React.ReactElement;
    hasSubMenu?: boolean;
    ListItemIconProps?: ListItemIconProps;
    loading?: boolean;
    renderSubMenu?: (menuProps: MenuProps) => React.ReactElement;
  }
}

const StyledSelectedIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.secondary[1],
  fontSize: theme.typography.small.fontSize,
}));

const MenuItemNoForward = <C extends React.ElementType>(
  props: MenuItemProps<C, { component?: C }>,
  ref: React.Ref<HTMLLIElement>,
) => {
  const {
    variant = "secondary",
    icon,
    endLabel,
    endIcon,
    disabled = false,
    loading = false,
    selected = false,
    showSelectedIcon = false,
    hasSubMenu = false,
    renderSubMenu,
    ListItemIconProps = {},
    children,
    ...rest
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenSubmenu = (e: React.MouseEvent<HTMLLIElement>) => {
    if (hasSubMenu) {
      setAnchorEl(e.currentTarget);
    }

    rest.onClick?.(e);
  };

  const handleCloseSubmenu = () => {
    setAnchorEl(null);
  };

  const hasEndStack = !!(endLabel || endIcon || hasSubMenu || showSelectedIcon);

  return (
    <>
      <MuiMenuItem
        variant={variant}
        selected={selected}
        disabled={disabled || loading}
        onClick={handleOpenSubmenu}
        {...rest}
        ref={ref}
      >
        {loading ? (
          <ListItemIcon>
            <Icon icon={faSpinnerThird} spin />
          </ListItemIcon>
        ) : (
          icon && <ListItemIcon {...ListItemIconProps}>{icon}</ListItemIcon>
        )}
        <Stack
          spacing={6}
          direction="row"
          sx={{
            alignItems: "center",
            flex: 1,
          }}
        >
          <ListItemText primary={children} />
          {hasEndStack && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: "center",
              }}
            >
              {endLabel && <ListItemText secondary={endLabel} />}
              {endIcon}
              {hasSubMenu && (
                <Box
                  sx={{
                    pr: 2,
                    color: "secondary.3",
                  }}
                >
                  <Icon size="small" icon={faCaretRight} />
                </Box>
              )}
              {showSelectedIcon && (
                <Box sx={{ opacity: selected ? 1 : 0, fontSize: 0 }}>
                  <StyledSelectedIcon icon={faCheck} />
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </MuiMenuItem>
      {renderSubMenu &&
        renderSubMenu({
          open: !!anchorEl,
          anchorEl,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          onClose: handleCloseSubmenu,
        })}
    </>
  );
};

export const MenuItem = forwardRef(MenuItemNoForward);
